// src/utils/pickBreakdownUtils.ts
import { normalizeTeamNameForDisplay } from './teamUtils';

export interface TeamPickCount {
  team: string;
  count: number;
  percentage: number; // 0-100
  entries: string[];  // entry names who picked this team
}

export interface GameBreakdown {
  gameId: number;
  round: number;
  region: string;
  totalEntries: number;
  picks: TeamPickCount[]; // sorted by count descending
  isOfficial: boolean;
  officialWinner?: string;
}

function getGameRound(gameId: number): number {
  if (gameId < 32) return 0;
  if (gameId < 48) return 1;
  if (gameId < 56) return 2;
  if (gameId < 60) return 3;
  if (gameId < 62) return 4;
  return 5;
}

function getGameRegion(gameId: number): string {
  if (gameId < 8) return 'South';
  if (gameId < 16) return 'East';
  if (gameId < 24) return 'West';
  if (gameId < 32) return 'Midwest';
  // Round of 32
  if (gameId < 36) return 'South';
  if (gameId < 40) return 'East';
  if (gameId < 44) return 'West';
  if (gameId < 48) return 'Midwest';
  // Sweet 16
  if (gameId < 50) return 'South';
  if (gameId < 52) return 'East';
  if (gameId < 54) return 'West';
  if (gameId < 56) return 'Midwest';
  // Elite 8
  if (gameId === 56) return 'South';
  if (gameId === 57) return 'East';
  if (gameId === 58) return 'West';
  if (gameId === 59) return 'Midwest';
  // Final Four + Championship
  if (gameId < 62) return 'Final Four';
  return 'Championship';
}

/**
 * Parse all player picks from the raw text, preserving index positions.
 * Unlike extractPlayerPicks, this does NOT filter empty strings so
 * pick indices align with game IDs.
 */
function parseAllPlayerPicks(playerPicksText: string): { name: string; picks: string[] }[] {
  if (!playerPicksText) return [];

  const lines = playerPicksText.trim().split('\n');
  const result: { name: string; picks: string[] }[] = [];

  for (const line of lines) {
    if (!line.trim()) continue;

    let match = line.match(/^"([^"]+)"\s*=\s*(.+)$/);
    if (!match) {
      match = line.match(/^(\w+)\s*=\s*(.+)$/);
    }

    if (match && match[1]) {
      const picks = match[2].split(',').map(pick => pick.trim());
      result.push({ name: match[1], picks });
    }
  }

  return result;
}

/**
 * Compute pick distributions for all 63 tournament games.
 * Returns a breakdown for each game showing how many entries picked each team.
 */
export function computePickBreakdowns(
  playerPicksText: string,
  officialWinners: string[]
): GameBreakdown[] {
  const allPicks = parseAllPlayerPicks(playerPicksText);
  if (allPicks.length === 0) return [];

  const breakdowns: GameBreakdown[] = [];

  for (let gameId = 0; gameId < 63; gameId++) {
    const teamCounts = new Map<string, string[]>();
    let validEntries = 0;

    for (const player of allPicks) {
      const pick = gameId < player.picks.length ? player.picks[gameId] : '';
      if (pick && pick !== '' && pick !== 'xxx') {
        const normalized = normalizeTeamNameForDisplay(pick);
        if (!teamCounts.has(normalized)) {
          teamCounts.set(normalized, []);
        }
        teamCounts.get(normalized)!.push(player.name);
        validEntries++;
      }
    }

    const picks: TeamPickCount[] = Array.from(teamCounts.entries())
      .map(([team, entries]) => ({
        team,
        count: entries.length,
        percentage: validEntries > 0 ? Math.round((entries.length / validEntries) * 100) : 0,
        entries
      }))
      .sort((a, b) => b.count - a.count);

    const isOfficial = gameId < officialWinners.length &&
      !!officialWinners[gameId] &&
      officialWinners[gameId].trim() !== '' &&
      officialWinners[gameId] !== 'xxx';

    breakdowns.push({
      gameId,
      round: getGameRound(gameId),
      region: getGameRegion(gameId),
      totalEntries: validEntries,
      picks,
      isOfficial,
      officialWinner: isOfficial ? normalizeTeamNameForDisplay(officialWinners[gameId]) : undefined
    });
  }

  return breakdowns;
}
